/**
 * Tiny localStorage-backed tracker for "this template has UILM key changes
 * that haven't been republished yet". Each template (keyed by slug) is
 * independent — a publish on template A doesn't affect template B's flag.
 *
 * Why localStorage: survives page reloads so the admin sees the dirty
 * indicator on returning to the page hours later. Cross-tab sync comes
 * for free via the native `storage` event; same-tab updates broadcast a
 * custom `uilm:unpublished:changed` event because `storage` only fires
 * for OTHER tabs.
 *
 * Usage in a React component → `useUnpublishedTracker(slug)`.
 */

const STORAGE_PREFIX = 'uilm:unpublished:';
export const UNPUBLISHED_CHANGED_EVENT = 'uilm:unpublished:changed';

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

/**
 * Mark a template as having unpublished changes. Call after any successful
 * sync that wrote keys to UILM but did NOT publish (every per-entity sync
 * helper after the per-save publish was removed).
 */
export function markUnpublished(slug: string): void {
  if (typeof window === 'undefined' || !slug) return;
  try {
    localStorage.setItem(storageKey(slug), '1');
    window.dispatchEvent(new CustomEvent(UNPUBLISHED_CHANGED_EVENT, { detail: { slug } }));
  } catch {
    // Quota / disabled storage — non-fatal; the tracker is best-effort.
  }
}

/**
 * Clear the unpublished flag. Call after `generateUilmFile` succeeds — i.e.
 * from every explicit Publish path (Translations Tab Publish button,
 * typeform-builder Publish button, Translations Tab Save Translations, seed).
 */
export function clearUnpublished(slug: string): void {
  if (typeof window === 'undefined' || !slug) return;
  try {
    localStorage.removeItem(storageKey(slug));
    window.dispatchEvent(new CustomEvent(UNPUBLISHED_CHANGED_EVENT, { detail: { slug } }));
  } catch {
    // Same as above.
  }
}

/** Synchronous read for one-off checks (initial render, server snapshot). */
export function hasUnpublished(slug: string): boolean {
  if (typeof window === 'undefined' || !slug) return false;
  try {
    return localStorage.getItem(storageKey(slug)) === '1';
  } catch {
    return false;
  }
}
